"use client";

import { useMemo, useCallback, useState, memo, useRef, useEffect, forwardRef } from "react";
import { motion } from "motion/react";

import { useNavbar } from "./navbar.context";
import { navbarPortalStyles, navbarPortalContentStyles } from "./styles/navbar-portal.styles";

import type { NavbarPortalProps } from "./types/navbar-portal.types";

const ANIMATION_DELAY = 100;
const SPRING_BOUNCE = 0.275;
const SPRING_DURATION = 0.45;

export const NavbarPortal = memo(
  forwardRef<HTMLDivElement, NavbarPortalProps>((props, ref) => {
    const { children, ...rest } = props;

    const [shouldAnimate, setShouldAnimate] = useState(false);

    const { value } = useNavbar();

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const inertProps = useMemo(() => {
      return !value ? { inert: true } : {};
    }, [value]);

    const isMenuOpen = !!value;

    const handleAnimationStart = useCallback(async () => {
      await new Promise((resolve) => setTimeout(resolve, ANIMATION_DELAY));
      timeoutRef.current = setTimeout(() => {
        setShouldAnimate(isMenuOpen);
      }, ANIMATION_DELAY);
    }, [isMenuOpen]);

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return (
      <div className={navbarPortalStyles({ isMenuOpen })}>
        <motion.div
          ref={ref}
          className={navbarPortalContentStyles()}
          {...inertProps}
          initial={false}
          animate={{
            opacity: isMenuOpen ? 1 : 0,
            y: isMenuOpen ? 0 : -10,
            pointerEvents: isMenuOpen ? "auto" : "none",
          }}
          transition={{ type: "spring", bounce: SPRING_BOUNCE, duration: SPRING_DURATION }}
          data-should-animate={shouldAnimate}
          onAnimationStart={handleAnimationStart}
          style={{ clipPath: "inset(-50% -50% -10px -50%)" }}
          {...rest}
        >
          {children}
        </motion.div>
      </div>
    );
  }),
);

NavbarPortal.displayName = "NavbarPortal";
